import React from 'react';
import { Button } from '../../../components/ui/Button';
import { Edit } from 'lucide-react';
import { ExtendedBrand } from '../BrandTypes';
import { getImageUrl, hasDocument } from '../BrandKycUtils';
import { deleteBrandDocument } from '../../../services/brand';
import { useAppSelector } from '../../../store/hooks/reduxHooks';
import { showToast } from '../../../utils/toast';

interface DocumentsKycTabProps {
  selectedBrand: ExtendedBrand;
  isDocsKycEditing: boolean;
  docsKycFiles: {shopFront: File | null; shopInner: File | null; evCer: File | null};
  errors: Record<string, string>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, docType: 'shopFront' | 'shopInner' | 'evCer') => void;
  setIsDocsKycEditing: React.Dispatch<React.SetStateAction<boolean>>;
  saveDocsKyc: () => void;
  isUpdating: boolean;
  canManageBrands: boolean;
  handleEditDocsKyc: () => void;
  setDocsKycFiles: React.Dispatch<React.SetStateAction<{shopFront: File | null; shopInner: File | null; evCer: File | null;}>>;
  handleDeleteConfirm: () => void;
}

export const DocumentsKycTab: React.FC<DocumentsKycTabProps> = ({
  selectedBrand,
  isDocsKycEditing,
  docsKycFiles,
  errors,
  handleFileChange,
  setIsDocsKycEditing,
  saveDocsKyc,
  isUpdating,
  canManageBrands,
  handleEditDocsKyc,
  setDocsKycFiles
}) => {
  // Get auth token from Redux store
  const { token } = useAppSelector(state => state.auth);
  const [deletingDoc, setDeletingDoc] = React.useState<string | null>(null);

  const handleRemoveDocument = async (docType: 'shopFront' | 'shopInner' | 'evCer') => {
    if (!selectedBrand || !selectedBrand.mobileNo) {
      showToast.error('Cannot delete document: Missing brand information');
      return;
    }
    
    try {
      setDeletingDoc(docType);
      
      // Call the new API function to delete the document
      const success = await deleteBrandDocument(
        selectedBrand.mobileNo,
        docType,
        token,
        true // Using mobile number
      );
      
      if (success) {
        showToast.success(`${docType} document deleted successfully`);
        // Force a refresh of the component to show updated document state
        setDocsKycFiles(prev => ({...prev}));
      }
    } catch (error) {
      console.error(`Error deleting ${docType} document:`, error);
      showToast.error(`Failed to delete ${docType} document`);
    } finally {
      setDeletingDoc(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-medium text-gray-700">Document KYC Details</h3>
        {selectedBrand.kyc?.docs && !isDocsKycEditing && canManageBrands && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleEditDocsKyc}
            className="text-xs"
          >
            <Edit size={14} className="mr-1" /> Update Documents
          </Button>
        )}
      </div>
      
      <div className="px-6 py-4">
        {isDocsKycEditing ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Shop Front Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shop Front Image
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                  <div className="space-y-1 text-center">
                    {docsKycFiles.shopFront ? (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Selected file: {docsKycFiles.shopFront.name}
                        </p>
                        <button
                          type="button"
                          onClick={() => setDocsKycFiles(prev => ({ ...prev, shopFront: null }))}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label
                            htmlFor="shopFront"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                          >
                            <span>Upload a file</span>
                            <input id="shopFront" name="shopFront" type="file" className="sr-only" onChange={(e) => handleFileChange(e, 'shopFront')} accept="image/*" />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Shop Inner Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shop Inner Image
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                  <div className="space-y-1 text-center">
                    {docsKycFiles.shopInner ? (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Selected file: {docsKycFiles.shopInner.name}
                        </p>
                        <button
                          type="button"
                          onClick={() => setDocsKycFiles(prev => ({ ...prev, shopInner: null }))}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label
                            htmlFor="shopInner"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                          >
                            <span>Upload a file</span>
                            <input id="shopInner" name="shopInner" type="file" className="sr-only" onChange={(e) => handleFileChange(e, 'shopInner')} accept="image/*" />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* EV Certificate (only for EV brands) */}
              {selectedBrand.isEv && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    EV Certificate
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      {docsKycFiles.evCer ? (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">
                            Selected file: {docsKycFiles.evCer.name}
                          </p>
                          <button
                            type="button"
                            onClick={() => setDocsKycFiles(prev => ({ ...prev, evCer: null }))}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div className="flex text-sm text-gray-600 justify-center">
                            <label
                              htmlFor="evCer"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                            >
                              <span>Upload a file</span>
                              <input id="evCer" name="evCer" type="file" className="sr-only" onChange={(e) => handleFileChange(e, 'evCer')} accept="image/*, application/pdf" />
                            </label>
                          </div>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, PDF up to 10MB
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {errors.documents && (
              <div className="rounded-md bg-red-50 p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{errors.documents}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsDocsKycEditing(false)}
              >
                Cancel
              </Button>
              <Button
                isLoading={isUpdating}
                onClick={saveDocsKyc}
              >
                {isUpdating ? 'Saving...' : 'Upload Documents'}
              </Button>
            </div>
          </div>
        ) : selectedBrand.kyc?.docs ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shop Front Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Front Photo
              </label>
              {hasDocument(selectedBrand, 'shopFront') ? (
                <div className="relative border border-gray-200 rounded-lg overflow-hidden group">
                  <img 
                    src={getImageUrl(selectedBrand, 'shopFront')} 
                    alt="Shop Front" 
                    className="w-full h-48 object-cover"
                  />
                  {canManageBrands && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100">
                      <Button
                        variant="outline"
                        color="red"
                        size="sm"
                        onClick={() => handleRemoveDocument('shopFront')}
                        isLoading={deletingDoc === 'shopFront'}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-500">
                  No shop front photo provided
                </div>
              )}
            </div>
            
            {/* Shop Inner Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Inner Photo
              </label>
              {hasDocument(selectedBrand, 'shopInner') ? (
                <div className="relative border border-gray-200 rounded-lg overflow-hidden group">
                  <img 
                    src={getImageUrl(selectedBrand, 'shopInner')} 
                    alt="Shop Inner" 
                    className="w-full h-48 object-cover"
                  />
                  {canManageBrands && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100">
                      <Button
                        variant="outline"
                        color="red"
                        size="sm"
                        onClick={() => handleRemoveDocument('shopInner')}
                        isLoading={deletingDoc === 'shopInner'}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-500">
                  No shop inner photo provided
                </div>
              )}
            </div>
            
            {/* EV Certificate (only for EV brands) */}
            {selectedBrand.isEv && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  EV Certificate
                </label>
                {hasDocument(selectedBrand, 'evCer') ? (
                  <div className="relative border border-gray-200 rounded-lg overflow-hidden group">
                    <img 
                      src={getImageUrl(selectedBrand, 'evCer')} 
                      alt="EV Certificate" 
                      className="w-full h-48 object-cover"
                    />
                    {canManageBrands && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100">
                        <Button
                          variant="outline"
                          color="red"
                          size="sm"
                          onClick={() => handleRemoveDocument('evCer')}
                          isLoading={deletingDoc === 'evCer'}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-500">
                    No EV certificate provided
                  </div>
                )}
              </div>
            )}
            
            {/* KYC Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document KYC Status
              </label>
              <div className="flex items-center">
                <span className="bg-green-200 text-green-900 px-2 py-1 rounded-full text-sm font-semibold border border-green-300">
                  Verified
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Documents Not Uploaded</h3>
            <p className="text-gray-500 mb-4">No shop photos or documents have been provided yet.</p>
            
            {canManageBrands && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleEditDocsKyc}
              >
                Upload Documents
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 